import { type Hex, InteractiveContainer, type Stroke } from '@acies/core';
import ButtonEvent from './button-event';
import Icon from './icon';
import { ICONS } from './icons';
import KeyboardShortcut from './keyboard-shortcut';
import Label from './label';
import theme from './theme';

/**
 * How the Button looks, state by state.
 *
 * Keyed by the styled state rather than the state, so hovering has no look of its own: the cursor is what says the Button can be pressed. Written out per state, so each can be read against the Design System and the compiler asks for a look the day a state is added.
 */
const LOOKS: Readonly<Record<Button['styledState'], { readonly fill: Hex; readonly stroke: Stroke | null }>> = {
	disabled: { fill: theme.colors.fill.weak, stroke: null },
	focused: { fill: theme.colors.fill.focus, stroke: theme.strokes.focus },
	focusedPressed: { fill: theme.colors.fill.white, stroke: theme.strokes.soft },
	idle: { fill: theme.colors.fill.white, stroke: theme.strokes.soft },
	pressed: { fill: theme.colors.fill.white, stroke: theme.strokes.soft },
};

/** The default outlined Button with possible icon, text and shortcut. */
export default class Button extends InteractiveContainer {
	public static override observedAttributes = [...InteractiveContainer.observedAttributes, 'icon', 'shortcut', 'text'];
	/**
	 * The icon is shown to the left and is helpful if you want to add further visual information.
	 *
	 * @param value The name of the icon you want to show.
	 */
	public set icon(value: keyof typeof ICONS | null) {
		if (this.#icon === value) {
			return;
		}
		assertIsValidIconName(value);
		this.#icon = value;
		this.#iconChanged = true;
		this.invalidateProperties();
	}

	public get icon(): keyof typeof ICONS | null {
		return this.#icon;
	}

	/**
	 * The shortcut text is positioned right, and indicates that a keyboard shortcut is available, for example "ESC" for the Escape key.
	 *
	 * @param {string} value The shortcut text you want to show.
	 */
	public set shortcut(value: string) {
		if (this.#shortcut === value) {
			return;
		}
		assertIsString(value);
		this.#shortcut = value;
		this.#shortcutChanged = true;
		this.invalidateProperties();
	}

	public get shortcut(): string {
		return this.#shortcut;
	}

	/** @param {string} value The Button text you want to show. */
	public set text(value: string) {
		if (this.#text === value) {
			return;
		}
		assertIsString(value);
		this.#text = value;
		this.#textChanged = true;
		this.invalidateProperties();
	}

	public get text(): string {
		return this.#text;
	}

	#icon: keyof typeof ICONS | null;
	#iconChanged: boolean;
	#iconChild: Icon;
	#labelChild: Label;
	#shortcut: string;
	#shortcutChanged: boolean;
	#shortcutChild: KeyboardShortcut;
	#text: string;
	#textChanged: boolean;

	public constructor() {
		super();
		this.height = 32;
		this.minWidth = 32;
		this.cornerRadius = 8;
		this.alignment = 'center';
		this.paddingHorizontal = 10;
		this.gapHorizontal = 10;
		this.cursor = 'pointer';
		this.fill = theme.colors.fill.white;
		this.stroke = theme.strokes.soft;
		this.#icon = null;
		this.#iconChanged = false;
		this.#iconChild = new Icon();
		this.#iconChild.visible = false;
		this.#labelChild = new Label();
		this.#labelChild.visible = false;
		this.#shortcut = '';
		this.#shortcutChanged = false;
		this.#shortcutChild = new KeyboardShortcut();
		this.#shortcutChild.visible = false;
		this.#text = '';
		this.#textChanged = false;
	}

	protected override attributeChangedCallback(name: string, _: string, value: string) {
		super.attributeChangedCallback(name, _, value);
		if (name === 'icon') {
			this.#iconAttributeChanged(value);
		} else if (name === 'shortcut') {
			this.#shortcutAttributeChanged(value);
		} else if (name === 'text') {
			this.#textAttributeChanged(value);
		}
	}

	protected override clicked(): void {
		this.dispatchEvent(new ButtonEvent());
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#textChanged || this.#iconChanged || this.#shortcutChanged) {
			this.#commitIconTextAndShortcut();
		}
	}

	protected override enabledChanged(): void {
		super.enabledChanged();
		if (this.enabled) {
			this.cursor = 'pointer';
		} else {
			this.cursor = 'default';
		}
	}

	protected override initialize(): void {
		super.initialize();
		this.addComponents([this.#iconChild, this.#labelChild, this.#shortcutChild]);
	}

	protected override stateChanged(): void {
		super.stateChanged();
		const look = LOOKS[this.styledState];
		this.fill = look.fill;
		this.stroke = look.stroke;
		this.#setChildrenVariants();
	}

	#commitIconTextAndShortcut(): void {
		this.#iconChanged = false;
		this.#textChanged = false;
		this.#shortcutChanged = false;
		this.#iconChild.name = this.icon;
		this.#labelChild.text = this.text;
		this.#shortcutChild.text = this.shortcut;
		this.#showHideChildrenFromProperties();
		this.#updatePaddingHorizontalFromProperties();
	}

	#iconAttributeChanged(value: string): void {
		assertIsValidIconName(value);
		this.icon = value;
	}

	#setChildrenVariants(): void {
		if (this.state === 'disabled') {
			this.#iconChild.variant = 'disabled';
			this.#labelChild.variant = 'disabled';
			this.#shortcutChild.variant = 'disabled';
		} else {
			this.#iconChild.variant = 'default';
			this.#labelChild.variant = 'default';
			this.#shortcutChild.variant = 'default';
		}
	}

	#shortcutAttributeChanged(value: string): void {
		this.shortcut = value;
	}

	#showHideChildrenFromProperties(): void {
		this.#iconChild.visible = this.icon !== null;
		this.#labelChild.visible = this.text !== '';
		this.#shortcutChild.visible = this.shortcut !== '';
	}

	#textAttributeChanged(value: string): void {
		this.text = value;
	}

	#updatePaddingHorizontalFromProperties(): void {
		if (this.icon !== null && this.text === '' && this.shortcut === '') {
			this.paddingHorizontal = 0;
		} else {
			this.paddingHorizontal = 10;
		}
	}
}
customElements.define('ac-button', Button);

function assertIsString(value: unknown): asserts value is string {
	if (typeof value === 'string') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type string`);
}

function assertIsValidIconName(value: unknown): asserts value is keyof typeof ICONS | null {
	if ((typeof value === 'string' && value in ICONS) || value === null) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type string and in ICONS object or null`);
}
